'use client';

import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { NavBar } from '@/components/navbar';
import { Footer } from '@/components/SiteFooter';
import { Button } from '@/components/ui/button';
import { ArrowUpDown, ExternalLink, Eye, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface LandingPage {
  path: string;
  name: string;
  description: string;
  created: string;
  category: string;
  status: 'active' | 'wip' | 'archive';
  tags: string[];
  conversionRate?: string;
}

type SortField = 'name' | 'created' | 'path';

// Helper function to format dates for sorting
const formatDateForSort = (dateString: string) => {
  const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));
  return new Date(year, month - 1, day).getTime();
};

const LandingDirectoryPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [landingPages, setLandingPages] = useState<LandingPage[]>([
    // Active landing pages
    {
      path: '/l/tmng',
      name: 'OPEXIA PSF Compliance Landing',
      description:
        'Tailored landing page for Nordine Garrouche at OPEXIA PSF SA, featuring Luxembourg-specific compliance solutions and AI transformation analysis.',
      created: '2025-03-29',
      category: 'Finance',
      status: 'wip',
      tags: ['Luxembourg', 'CSSF', 'REGTECH', 'AI Transformation'],
      conversionRate: '9.2%',
    },
    {
      path: '/l/teamwork-inspired',
      name: 'Teamwork-Inspired Landing Page',
      description:
        'Modern enterprise-focused landing page with Teamwork.com-inspired design showcasing compliance workflow platform and AI-powered features.',
      created: '2025-03-28',
      category: 'Enterprise',
      status: 'wip',
      tags: ['Compliance', 'Workflow', 'Enterprise', 'AI'],
      conversionRate: '8.3%',
    },
    {
      path: '/l/smallcta',
      name: 'Compliance AI CTA Page',
      description:
        'Feature-rich landing page with compliance AI form, interactive product features, and enterprise advantages showcase.',
      created: '2025-03-27',
      category: 'Business',
      status: 'active',
      tags: ['Compliance', 'AI', 'Solutions', 'Features'],
      conversionRate: '6.2%',
    },
    {
      path: '/l/ctaform',
      name: 'Compliance AI Form Page',
      description:
        'Focused AI compliance form with detailed analysis capabilities and visualization of process improvements.',
      created: '2025-03-25',
      category: 'Conversion',
      status: 'active',
      tags: ['Compliance', 'Form', 'AI Analysis', 'Process Optimization'],
      conversionRate: '7.4%',
    },
    {
      path: '/l/23/finance-marketing',
      name: 'Financial Marketing Compliance',
      description:
        'Comprehensive landing page for financial marketing compliance solutions across multiple regulatory frameworks.',
      created: '2025-03-10',
      category: 'Finance',
      status: 'active',
      tags: ['Finance', 'Marketing', 'Compliance', 'Multi-Regulatory'],
      conversionRate: '4.8%',
    },

    // Work in progress landing pages
    {
      path: '/l/minimal',
      name: 'Minimal Landing Page',
      description:
        "A clean, minimal landing page with just the essential navbar for testing purposes or minimal content display.",
      created: '2025-03-29',
      category: 'UI Templates',
      status: 'wip',
      tags: ['Minimal', 'Template', 'Clean', 'Navbar Only'],
      conversionRate: 'N/A',
    },
    {
      path: '/l/24/finance-marketing-wilson',
      name: 'Financial Marketing Compliance - Wilson',
      description:
        "Enterprise financial marketing compliance solution with Wilson AI-enhanced visualization and compliance process optimization.",
      created: '2025-03-20',
      category: 'Finance',
      status: 'wip',
      tags: ['Finance', 'Marketing', 'Compliance', 'Wilson-Style'],
      conversionRate: '5.8%',
    },
    {
      path: '/l/24/luthor-ai',
      name: 'Enterprise AI Agents Platform',
      description:
        "Enterprise-grade AI agents platform landing page showcasing Luthor AI's capabilities for building intelligent assistants for businesses.",
      created: '2025-03-20',
      category: 'AI',
      status: 'wip',
      tags: ['AI', 'Agents', 'Enterprise', 'Luthor AI'],
      conversionRate: '3.7%',
    },

    // Archive landing pages (including former testing pages)
    {
      path: '/l/24/verc',
      name: 'VERC Compliance Platform',
      description:
        'Enterprise verification and regulatory compliance platform landing page for financial institutions.',
      created: '2025-02-28',
      category: 'Compliance',
      status: 'archive',
      tags: ['Verification', 'Regulatory', 'Compliance', 'Enterprise'],
      conversionRate: '2.9%',
    },
    {
      path: '/l/22/aifmd',
      name: 'AIFMD Compliance v2.2',
      description:
        'AIFMD compliance landing page with improved conversion elements and optimized form placement.',
      created: '2024-02-10',
      category: 'Finance',
      status: 'archive',
      tags: ['AIFMD', 'Compliance', 'v2.2'],
      conversionRate: '5.7%',
    },
    {
      path: '/l/23/aifmd',
      name: 'AIFMD Compliance v2.3',
      description:
        'Updated AIFMD compliance landing page with improved testimonials and feature highlighting for financial institutions.',
      created: '2024-11-20',
      category: 'Finance',
      status: 'archive',
      tags: ['AIFMD', 'Compliance', 'v2.3'],
      conversionRate: '6.2%',
    },
    {
      path: '/l/AIFMDMarketing',
      name: 'AIFMD Marketing Campaign',
      description:
        'Special campaign landing page focused on AIFMD compliance solutions with emphasis on marketing compliance aspects.',
      created: '2024-09-12',
      category: 'Finance',
      status: 'archive',
      tags: ['AIFMD', 'Marketing', 'Campaign', 'Compliance'],
      conversionRate: '5.3%',
    },
    {
      path: '/l/20/aifmd',
      name: 'AIFMD Compliance v2.0',
      description:
        'AIFMD compliance landing page with interactive compliance assessment tool for alternative investment firms.',
      created: '2023-04-15',
      category: 'Finance',
      status: 'archive',
      tags: ['AIFMD', 'Compliance', 'v2.0'],
      conversionRate: '3.1%',
    }
  ]);

  // Get unique categories for filter dropdown
  const categories = useMemo(() => {
    const uniqueCategories = new Set<string>();
    landingPages.forEach(page => uniqueCategories.add(page.category));
    return Array.from(uniqueCategories).sort();
  }, [landingPages]);

  // Create a sorted and filtered version of the landing pages
  const sortedPages = useMemo(() => {
    return [...landingPages]
      .filter(
        page =>
          // Text search filter
          (searchTerm
            ? page.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              page.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
              page.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
            : true) &&
          // Category filter
          (categoryFilter === 'all' || page.category === categoryFilter)
      )
      .sort((a, b) => {
        let aValue, bValue;

        switch (sortField) {
          case 'name':
            aValue = a.name;
            bValue = b.name;
            break;
          case 'created':
            aValue = formatDateForSort(a.created);
            bValue = formatDateForSort(b.created);
            break;
          case 'path':
            aValue = a.path;
            bValue = b.path;
            break;
          default:
            aValue = a.name;
            bValue = b.name;
        }

        if (sortDirection === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
  }, [landingPages, sortField, sortDirection, searchTerm, categoryFilter]);

  // Handle the sort column click
  const handleSortClick = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Active pages - based on status property
  const activePages = useMemo(() => {
    return sortedPages.filter(page => page.status === 'active');
  }, [sortedPages]);

  // Work in progress pages
  const wipPages = useMemo(() => {
    return sortedPages.filter(page => page.status === 'wip');
  }, [sortedPages]);

  // Archive pages
  const archivePages = useMemo(() => {
    return sortedPages.filter(page => page.status === 'archive');
  }, [sortedPages]);

  // Render a landing page table
  const renderLandingPagesTable = (pages: LandingPage[], title: string) => (
    <div className="mb-12">
      <div className="mb-4">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-bold">{title}</h2>
          <p className="text-sm text-gray-500">Showing {pages.length} landing pages</p>
        </div>
      </div>

      <div className="bg-white rounded-md border shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead
                className="w-[300px] cursor-pointer"
                onClick={() => handleSortClick('name')}
              >
                <div className="flex items-center gap-1">
                  Name
                  <ArrowUpDown className="h-3 w-3" />
                </div>
              </TableHead>
              <TableHead>Description</TableHead>
              <TableHead
                className="w-[120px] cursor-pointer"
                onClick={() => handleSortClick('created')}
              >
                <div className="flex items-center gap-1">
                  Created
                  <ArrowUpDown className="h-3 w-3" />
                </div>
              </TableHead>
              <TableHead className="w-[100px] text-center">Category</TableHead>
              <TableHead className="w-[120px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pages.length > 0 ? (
              pages.map((page, i) => (
                <TableRow key={i} className="group hover:bg-gray-50">
                  <TableCell>
                    <div className="font-medium">{page.name}</div>
                    <div className="text-xs text-gray-500 mt-1">{page.path}</div>
                    <div className="flex mt-1 gap-1 flex-wrap">
                      {page.tags.map((tag, i) => (
                        <Badge key={i} variant="outline" className="text-xs px-1.5 py-0.5">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{page.description}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{page.created}</TableCell>
                  <TableCell className="text-sm text-center">
                    <div className="flex flex-col gap-1 items-center">
                      <Badge variant="secondary" className="font-normal">
                        {page.category}
                      </Badge>
                      <Badge 
                        variant={
                          page.status === 'active' ? 'success' : 
                          page.status === 'wip' ? 'warning' : 
                          'destructive'
                        } 
                        className="text-xs font-normal">
                        {page.status}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end opacity-70 group-hover:opacity-100">
                      <Link to={page.path} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <ExternalLink className="h-4 w-4 text-gray-700" />
                        </Button>
                      </Link>

                      <Link to={page.path}>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Eye className="h-4 w-4 text-gray-700" />
                        </Button>
                      </Link>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  No landing pages found matching your search.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Landing Pages Directory | Verdea AI</title>
        <meta
          name="description"
          content="Directory of all landing pages available in the system."
        />
      </Helmet>

      <header>
        <NavBar />
      </header>

      <main className="flex-grow py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="mb-6">
              <h1 className="text-2xl font-bold mb-1">Landing Pages Directory</h1>
              <p className="text-gray-600">A list of all landing pages in the system</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="relative flex-grow">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
                <Input
                  placeholder="Search landing pages..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Active Landing Pages Table */}
            {activePages.length > 0 && renderLandingPagesTable(activePages, 'Active Landing Pages')}
            
            {/* Work in Progress Landing Pages Table */}
            {wipPages.length > 0 && renderLandingPagesTable(wipPages, 'Work in Progress (WIP) Landing Pages')}
            
            {/* Archive Landing Pages Table */}
            {archivePages.length > 0 && renderLandingPagesTable(archivePages, 'Archive Landing Pages')}
          </div>
        </div>
      </main>

      <footer>
        <Footer />
      </footer>
    </div>
  );
};

export default LandingDirectoryPage;
